import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface OnboardingState {
  hasCompletedOnboarding: boolean;
  hasCreatedFirstOrder: boolean;
  hasSeenSmartInput: boolean;
  hasSeenFeatureComparison: boolean;
  lastSeenVersion: string;
  hasMarkedOrderReady: boolean;
  hasViewedArchive: boolean;
  hasViewedDashboard: boolean;
  checklistDismissed: boolean;
}

interface OnboardingContextValue {
  state: OnboardingState;
  markOnboardingComplete: () => void;
  markFirstOrderCreated: () => void;
  markSmartInputSeen: () => void;
  markFeatureComparisonSeen: () => void;
  markOrderReady: () => void;
  markArchiveViewed: () => void;
  markDashboardViewed: () => void;
  dismissChecklist: () => void;
  resetOnboarding: () => void;
  checklistItems: { key: string; completed: boolean }[];
  completedCount: number;
  totalCount: number;
  isChecklistComplete: boolean;
  // Guide modal state (lifted here so sidebar can trigger it)
  showOnboarding: boolean;
  setShowOnboarding: (show: boolean) => void;
}

const STORAGE_KEY = "orderflow-onboarding";
const CURRENT_VERSION = "1.2.0";

const defaultState: OnboardingState = {
  hasCompletedOnboarding: false,
  hasCreatedFirstOrder: false,
  hasSeenSmartInput: false,
  hasSeenFeatureComparison: false,
  lastSeenVersion: CURRENT_VERSION,
  hasMarkedOrderReady: false,
  hasViewedArchive: false,
  hasViewedDashboard: false,
  checklistDismissed: false,
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultState, ...JSON.parse(saved) } : defaultState;
    } catch {
      return defaultState;
    }
  });
  
  // Guide modal state - lifted here so sidebar can trigger it
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const markOnboardingComplete = () => {
    setState((prev) => ({ ...prev, hasCompletedOnboarding: true }));
  };

  const markFirstOrderCreated = () => {
    setState((prev) => ({ ...prev, hasCreatedFirstOrder: true }));
  };

  const markSmartInputSeen = () => {
    setState((prev) => ({ ...prev, hasSeenSmartInput: true }));
  };

  const markFeatureComparisonSeen = () => {
    setState((prev) => ({ ...prev, hasSeenFeatureComparison: true }));
  };

  const markOrderReady = () => {
    try {
      setState((prev) => ({ ...prev, hasMarkedOrderReady: true }));
    } catch (e) {
      console.error("Failed to mark order ready:", e);
    }
  };

  const markArchiveViewed = () => {
    try {
      setState((prev) => ({ ...prev, hasViewedArchive: true }));
    } catch (e) {
      console.error("Failed to mark archive viewed:", e);
    }
  };

  const markDashboardViewed = () => {
    try {
      setState((prev) => ({ ...prev, hasViewedDashboard: true }));
    } catch (e) {
      console.error("Failed to mark dashboard viewed:", e);
    }
  };

  const dismissChecklist = () => {
    try {
      setState((prev) => ({ ...prev, checklistDismissed: true }));
    } catch (e) {
      console.error("Failed to dismiss checklist:", e);
    }
  };

  const resetOnboarding = () => {
    setState(defaultState);
  };

  // Calculate checklist progress
  const checklistItems = [
    { key: "hasCreatedFirstOrder", completed: state.hasCreatedFirstOrder },
    { key: "hasMarkedOrderReady", completed: state.hasMarkedOrderReady },
    { key: "hasViewedArchive", completed: state.hasViewedArchive },
    { key: "hasViewedDashboard", completed: state.hasViewedDashboard },
  ];

  const completedCount = checklistItems.filter((item) => item.completed).length;
  const totalCount = checklistItems.length;
  const isChecklistComplete = completedCount === totalCount;

  const contextValue: OnboardingContextValue = {
    state,
    markOnboardingComplete,
    markFirstOrderCreated,
    markSmartInputSeen,
    markFeatureComparisonSeen,
    markOrderReady,
    markArchiveViewed,
    markDashboardViewed,
    dismissChecklist,
    resetOnboarding,
    checklistItems,
    completedCount,
    totalCount,
    isChecklistComplete,
    showOnboarding,
    setShowOnboarding,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
}
